/* 
🌌 Shell UI Design System - Uptime Counter Component
Purpose: Shell system uptime counter with proper formatting

Features:
- Fetches actual shell uptime from backend /health endpoint
- Formats uptime as human-readable text (e.g., "5min", "2h 30min", "1d 5h")
- Omits zero values (doesn't show "0d" or "0h")
- Auto-refreshes every minute
- Falls back to frontend uptime if backend unavailable
*/

import React, { useState, useEffect } from 'react';

// Format uptime in human-readable format, omitting zero values
const formatUptime = (totalMinutes) => {
  if (!totalMinutes || totalMinutes < 1) return 'just now';
  
  const years = Math.floor(totalMinutes / (365 * 24 * 60));
  const months = Math.floor((totalMinutes % (365 * 24 * 60)) / (30 * 24 * 60));
  const days = Math.floor((totalMinutes % (30 * 24 * 60)) / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = Math.floor(totalMinutes % 60);
  
  const parts = [];
  
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}mo`);
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}min`);
  
  // Return the first 2 most significant parts
  return parts.slice(0, 2).join(' ') || 'just now';
};

// Fetch shell uptime from backend
const fetchShellUptime = async () => {
  try {
    // Try backend health endpoint first
    const response = await fetch('/health', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });
    
    if (response.ok) {
      const data = await response.json();
      
      // Check if backend provides uptime info
      if (data.uptime_minutes) {
        return data.uptime_minutes;
      }
      
      // Check for container start time
      if (data.started_at) {
        const startTime = new Date(data.started_at);
        const elapsedMs = Date.now() - startTime.getTime();
        return Math.floor(elapsedMs / 60000); // Convert to minutes
      }
    }
  } catch (error) {
    // Backend unavailable, will fall back
  }
  
  // Fallback: Use page load time (better than nothing)
  const pageLoadTime = window.performance?.timing?.navigationStart || Date.now();
  const elapsedMs = Date.now() - pageLoadTime;
  return Math.floor(elapsedMs / 60000);
};

export const UptimeCounter = ({ 
  className = '',
  showLabel = true,
  refreshInterval = 60000, // Refresh every minute
  ...props 
}) => {
  const [uptime, setUptime] = useState('loading...');
  const [error, setError] = useState(false);

  useEffect(() => {
    const updateUptime = async () => {
      try {
        const uptimeMinutes = await fetchShellUptime();
        const formattedUptime = formatUptime(uptimeMinutes);
        setUptime(formattedUptime);
        setError(false);
      } catch (err) {
        console.warn('Failed to fetch uptime:', err);
        setUptime('unknown');
        setError(true);
      }
    };

    // Initial load
    updateUptime();
    
    // Set up refresh interval
    const interval = setInterval(updateUptime, refreshInterval);
    
    return () => clearInterval(interval);
  }, [refreshInterval]);

  return (
    <div className={`shell-uptime-container ${error ? 'shell-uptime-error' : ''} ${className}`} {...props}>
      {showLabel && (
        <span className="shell-uptime-label">uptime</span>
      )}
      <span className="shell-uptime-value">{uptime}</span>
    </div>
  );
};

export default UptimeCounter;